import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { paths } from "../routes/paths";
import Layout from "../layouts/layout";
import SidePanel from "../components/sidePanel";
import Loader from "../components/loader";
import BILLING from "../api/services/BILLING";
import CUSTOMERS from "../api/services/CUSTOMERS";
import PlanDetailPanel from "../containers/_clientBilling/planDetailPanel";
import styles from "../styles/_screens/clientBilling.module.scss";

const DOC_TYPE_LABELS = {
  receipt: "קבלה",
  invoice_receipt: "חשבונית מס קבלה",
  invoice: "חשבונית מס",
  // Morning API returns numeric types
  400: "קבלה",
  305: "חשבונית מס קבלה",
  320: "חשבונית מס",
};

const PAYMENT_METHOD_LABELS = {
  credit: "אשראי",
  cash: "מזומן",
  bank: "העברה בנקאית",
  check: "צ'ק",
  other: "אחר",
};

const ClientBilling = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "accounting" ? "accounting" : tabParam === "purchases" ? "purchases" : "charges";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [client, setClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);

  // CHARGES TAB STATE
  const [isChargeFormOpen, setIsChargeFormOpen] = useState(false);
  const [chargeSaving, setChargeSaving] = useState(false);
  const [chargeForm, setChargeForm] = useState({
    reason: "",
    amount: "",
    cardNumber: "",
    exp: "",
    cvv: "",
    holderId: "",
    fullName: "",
    companyId: "",
    address: "",
    city: "",
    invoicePhone: "",
    email: "",
  });

  // ACCOUNTING TAB STATE
  const [morningAccount, setMorningAccount] = useState(null);
  const [morningLoading, setMorningLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [isInvoiceFormOpen, setIsInvoiceFormOpen] = useState(false);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    clientAddress: "",
    clientCity: "",
    description: "",
    amount: "",
    docType: "",
    paymentMethod: "credit",
    clientTaxId: "",
  });

  // PURCHASES TAB STATE
  const [clientPurchases, setClientPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPlanDetailOpen, setIsPlanDetailOpen] = useState(false);

  // FETCH CLIENT
  useEffect(() => {
    const fetchClient = async () => {
      setClientLoading(true);
      try {
        const res = await CUSTOMERS.get_by_id(id);
        const raw = res.data || res;
        // API returns UserBusiness doc with populated userId
        const clientData = {
          ...raw,
          name: raw.userId?.name || raw.name || "",
          phone: raw.phone || raw.userId?.phone || "",
          profile: raw.userId?.profile || raw.profile || "",
        };
        setClient(clientData);
        setInvoiceForm((prev) => ({
          ...prev,
          clientName: clientData.name,
          clientPhone: clientData.phone,
        }));
      } catch (error) {
        console.log(error);
        toast.error(error.response?.data?.message || "שגיאה בטעינת פרטי לקוח");
      }
      setClientLoading(false);
    };
    fetchClient();
  }, [id]);

  // FETCH MORNING ACCOUNT + INVOICES WHEN ACCOUNTING TAB IS ACTIVE
  useEffect(() => {
    if (activeTab !== "accounting") return;

    const fetchMorning = async () => {
      setMorningLoading(true);
      try {
        const res = await BILLING.get_morning_account();
        setMorningAccount(res);
      } catch (error) {
        console.log(error);
        setMorningAccount({ connected: false });
      }
      setMorningLoading(false);
    };

    fetchMorning();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "accounting" || !client) return;

    const fetchInvoices = async () => {
      setInvoicesLoading(true);
      try {
        const res = await BILLING.get_invoices_list({
          clientName: client.name,
          page: 1,
          pageSize: 30,
        });
        setInvoices(res.data || []);
      } catch (error) {
        console.log(error);
        setInvoices([]);
      }
      setInvoicesLoading(false);
    };

    fetchInvoices();
  }, [activeTab, client]);

  const fetchClientPurchases = async () => {
    setPurchasesLoading(true);
    try {
      const res = await BILLING.get_client_purchases(id);
      const items = res?.plans || res?.data || [];
      setClientPurchases(Array.isArray(items) ? items : []);
    } catch (error) {
      console.log(error);
      toast.error("שגיאה בטעינת רכישות");
      setClientPurchases([]);
    }
    setPurchasesLoading(false);
  };

  // FETCH CLIENT PURCHASES WHEN PURCHASES TAB IS ACTIVE
  useEffect(() => {
    if (activeTab !== "purchases" || !id) return;

    fetchClientPurchases();
  }, [activeTab, id]);

  // HANDLE CHARGE FORM CHANGE
  const handleChargeChange = (e) => {
    setChargeForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // SUBMIT CHARGE
  const handleChargeSubmit = async () => {
    if (!chargeForm.reason || !chargeForm.amount || !chargeForm.cardNumber) {
      toast.error("נא למלא את שדות החובה");
      return;
    }
    setChargeSaving(true);
    try {
      await BILLING.make_payment_for_client({
        reason: chargeForm.reason,
        userId: id,
        amount: chargeForm.amount,
        cvv: chargeForm.cvv,
        holderId: chargeForm.holderId,
        exp: chargeForm.exp,
        cardNumber: chargeForm.cardNumber,
        fullName: chargeForm.fullName,
        companyId: chargeForm.companyId,
        address: chargeForm.address,
        city: chargeForm.city,
        invoicePhone: chargeForm.invoicePhone,
        email: chargeForm.email,
      });
      toast.success("החיוב בוצע בהצלחה!");
      setIsChargeFormOpen(false);
      setChargeForm({
        reason: "",
        amount: "",
        cardNumber: "",
        exp: "",
        cvv: "",
        holderId: "",
        fullName: "",
        companyId: "",
        address: "",
        city: "",
        invoicePhone: "",
        email: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "שגיאה בביצוע החיוב");
    }
    setChargeSaving(false);
  };

  // HANDLE INVOICE FORM CHANGE
  const handleInvoiceChange = (e) => {
    setInvoiceForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // SUBMIT INVOICE
  const handleInvoiceSubmit = async () => {
    if (!invoiceForm.amount || !invoiceForm.docType) {
      toast.error("נא למלא סכום וסוג מסמך");
      return;
    }
    setInvoiceSaving(true);
    try {
      await BILLING.create_invoice({
        clientName: invoiceForm.clientName,
        clientEmail: invoiceForm.clientEmail,
        clientPhone: invoiceForm.clientPhone,
        clientAddress: invoiceForm.clientAddress,
        clientCity: invoiceForm.clientCity,
        // clientTaxId is optional — only send when the user filled it in
        ...(invoiceForm.clientTaxId && { clientId: invoiceForm.clientTaxId }),
        description: invoiceForm.description,
        amount: invoiceForm.amount,
        docType: invoiceForm.docType,
        paymentMethod: invoiceForm.paymentMethod,
      });
      toast.success("המסמך הופק בהצלחה!");
      setIsInvoiceFormOpen(false);
      // Refresh invoices list
      const res = await BILLING.get_invoices_list({
        clientName: client.name,
        page: 1,
        pageSize: 30,
      });
      setInvoices(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "שגיאה בהפקת המסמך");
    }
    setInvoiceSaving(false);
  };

  const getFirstLetter = (name) => {
    if (!name) return "?";
    return name.charAt(0).toUpperCase();
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy");
    } catch {
      return dateStr;
    }
  };

  const getDocAmount = (doc) => {
    if (doc.income && doc.income.length > 0) {
      return doc.income.reduce((sum, item) => sum + (item.price || 0), 0);
    }
    return 0;
  };

  if (clientLoading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <Loader />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          {/* BACK BUTTON */}
          <button className={styles.backBtn} onClick={() => navigate(paths.clients)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            חזרה ללקוחות
          </button>

          {/* CLIENT HEADER */}
          {client && (
            <div className={styles.clientHeader}>
              <div className={styles.avatar}>{getFirstLetter(client.name)}</div>
              <div className={styles.clientInfo}>
                <h2>{client.name}</h2>
                <p>{client.phone}</p>
              </div>
            </div>
          )}

          {/* TABS */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === "charges" ? styles.active : ""}`}
              onClick={() => setActiveTab("charges")}
            >
              חיובים
            </button>
            <button
              className={`${styles.tab} ${activeTab === "accounting" ? styles.active : ""}`}
              onClick={() => setActiveTab("accounting")}
            >
              הנה"ח
            </button>
            <button
              className={`${styles.tab} ${activeTab === "purchases" ? styles.active : ""}`}
              onClick={() => setActiveTab("purchases")}
            >
              רכישות
            </button>
          </div>

          {/* CHARGES TAB */}
          {activeTab === "charges" && (
            <div className={styles.tabContent}>
              <div className={styles.actionBar}>
                <button
                  className={styles.primaryBtn}
                  onClick={() => setIsChargeFormOpen(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                  חיוב ידני
                </button>
              </div>

              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="5" width="20" height="14" rx="3" stroke="#DE1468" strokeWidth="1.5" />
                    <path d="M2 10h20" stroke="#DE1468" strokeWidth="1.5" />
                    <rect x="5" y="14" width="4" height="2" rx="1" fill="#DE1468" />
                  </svg>
                </div>
                <h3>אין חיובים עדיין</h3>
                <p>לחץ על "+ חיוב ידני" כדי לבצע חיוב ראשון</p>
              </div>
            </div>
          )}

          {/* ACCOUNTING TAB */}
          {activeTab === "accounting" && (
            <div className={styles.tabContent}>
              {morningLoading ? (
                <Loader />
              ) : (
                <>
                  {/* WARNING IF MORNING NOT CONFIGURED */}
                  {morningAccount && !morningAccount.connected && (
                    <div className={styles.warningBox}>
                      <svg className={styles.warningIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#ea580c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <p>
                        חשבונית ירוקה אינה מוגדרת.{" "}
                        <span onClick={() => navigate(paths.paymentSettings)}>
                          עבור להגדרות סליקה
                        </span>{" "}
                        להגדרת חיבור
                      </p>
                    </div>
                  )}

                  <div className={styles.actionBar}>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => setIsInvoiceFormOpen(true)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                      הפקת מסמך
                    </button>
                  </div>

                  {/* DOCUMENTS TABLE */}
                  {invoicesLoading ? (
                    <Loader />
                  ) : invoices.length === 0 ? (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyIcon}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#DE1468" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M14 2v6h6M16 13H8M16 17H8" stroke="#DE1468" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <h3>אין מסמכים עדיין</h3>
                      <p>לחץ על "+ הפקת מסמך" כדי ליצור מסמך ראשון</p>
                    </div>
                  ) : (
                    <div className={styles.tableWrapper}>
                      <table>
                        <thead>
                          <tr>
                            <th>סוג מסמך</th>
                            <th>מספר</th>
                            <th>לקוח</th>
                            <th>תיאור</th>
                            <th>סכום</th>
                            <th>תאריך</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((doc) => (
                            <tr key={doc.id}>
                              <td data-label="סוג מסמך">
                                <span className={styles.docType}>
                                  {DOC_TYPE_LABELS[doc.type] || doc.type}
                                </span>
                              </td>
                              <td data-label="מספר">{doc.number || "-"}</td>
                              <td data-label="לקוח">{doc.client?.name || "-"}</td>
                              <td data-label="תיאור">{doc.description || "-"}</td>
                              <td data-label="סכום">
                                ₪{getDocAmount(doc).toLocaleString("he-IL")}
                              </td>
                              <td data-label="תאריך">
                                {doc.date ? formatDate(doc.date) : "-"}
                              </td>
                              <td>
                                {doc.viewUrl ? (
                                  <a
                                    href={doc.viewUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={styles.viewBtn}
                                  >
                                    צפה
                                  </a>
                                ) : (
                                  <span style={{ color: "#94a3b8", fontSize: "13px" }}>-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* PURCHASES TAB */}
          {activeTab === "purchases" && (
            <div className={styles.tabContent}>
              {purchasesLoading ? (
                <Loader />
              ) : clientPurchases.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="#DE1468" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="3" y1="6" x2="21" y2="6" stroke="#DE1468" strokeWidth="1.5" />
                      <path d="M16 10a4 4 0 01-8 0" stroke="#DE1468" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3>{"\u05D0\u05D9\u05DF \u05E8\u05DB\u05D9\u05E9\u05D5\u05EA"}</h3>
                  <p>{"\u05DC\u05DC\u05E7\u05D5\u05D7 \u05D6\u05D4 \u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05E8\u05DB\u05D9\u05E9\u05D5\u05EA \u05DE\u05E0\u05D5\u05D9\u05D9\u05DD \u05D0\u05D5 \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05D5\u05EA"}</p>
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th>{"\u05DE\u05E0\u05D5\u05D9/\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05D4"}</th>
                        <th>{"\u05E1\u05D5\u05D2"}</th>
                        <th>{"\u05DE\u05D7\u05D9\u05E8"}</th>
                        <th>{"\u05EA\u05D0\u05E8\u05D9\u05DA \u05E8\u05DB\u05D9\u05E9\u05D4"}</th>
                        <th>{"\u05E1\u05D8\u05D8\u05D5\u05E1"}</th>
                        <th>{"\u05E0\u05D5\u05EA\u05E8\u05D5"}</th>
                        <th>{"\u05EA\u05D5\u05E7\u05E3"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientPurchases.map((plan) => {
                        const isSub = plan.isSubscription || plan.baseServicePlanId?.isSubscription;
                        const amount = plan.price ?? 0;
                        const isFree = amount === 0;
                        const isExpired = plan.expiresAt && new Date(plan.expiresAt) < new Date();
                        const statusKey = plan.isFrozen ? "frozen" : (!plan.isActive || isExpired) ? "expired" : "active";
                        const remaining = plan.numberOfTurns > 0
                          ? plan.numberOfTurns - (plan.consumedOneTimeTurns || 0)
                          : null;

                        return (
                          <tr key={plan._id} className={styles.tableRow} style={{ cursor: "pointer" }} onClick={() => { setSelectedPlan(plan); setIsPlanDetailOpen(true); }}>
                            <td data-label="מנוי/כרטיסיה">
                              <strong>{plan.name || plan.baseServicePlanId?.name || "-"}</strong>
                            </td>
                            <td data-label="סוג">
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 600,
                                background: isSub ? "rgba(255,182,182,0.25)" : "rgba(59,130,246,0.12)",
                                border: isSub ? "1px solid #e40000" : "1px solid #3b82f6",
                                color: isSub ? "#e40000" : "#2563eb",
                              }}>
                                {isSub ? "מנוי חודשי" : "כרטיסיה"}
                              </span>
                            </td>
                            <td data-label="מחיר">
                              <span style={{ color: isFree ? "#06a816" : undefined, fontWeight: isFree ? 600 : undefined }}>
                                {isFree ? "חינם" : `₪${amount}`}
                              </span>
                            </td>
                            <td data-label="תאריך רכישה">
                              {plan.paidAt ? formatDate(plan.paidAt) : plan.createdAt ? formatDate(plan.createdAt) : "-"}
                            </td>
                            <td data-label="סטטוס">
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 600,
                                background: statusKey === "active"
                                  ? "rgba(182,255,189,0.36)"
                                  : statusKey === "frozen"
                                  ? "rgba(59,130,246,0.12)"
                                  : "rgba(255,182,182,0.36)",
                                border: statusKey === "active"
                                  ? "1px solid #06a816"
                                  : statusKey === "frozen"
                                  ? "1px solid #3b82f6"
                                  : "1px solid #e40000",
                                color: statusKey === "active"
                                  ? "#06a816"
                                  : statusKey === "frozen"
                                  ? "#2563eb"
                                  : "#e40000",
                              }}>
                                {statusKey === "active" ? "פעיל" : statusKey === "frozen" ? "מוקפא" : "פג תוקף"}
                              </span>
                            </td>
                            <td data-label="נותרו">
                              {remaining !== null
                                ? `${remaining} / ${plan.numberOfTurns}`
                                : "-"}
                            </td>
                            <td data-label="תוקף">
                              {plan.expiresAt ? formatDate(plan.expiresAt) : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* CHARGE SIDE PANEL */}
      {isChargeFormOpen && (
        <SidePanel setIsOpen={setIsChargeFormOpen} title="חיוב ידני">
          <div className={styles.formSection}>
            <div className={styles.fieldGroup}>
              <label>סיבת חיוב *</label>
              <input
                name="reason"
                type="text"
                placeholder="לדוגמה: טיפול, מנוי..."
                value={chargeForm.reason}
                onChange={handleChargeChange}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>סכום (₪) *</label>
              <input
                name="amount"
                type="number"
                placeholder="0.00"
                value={chargeForm.amount}
                onChange={handleChargeChange}
              />
            </div>

            <div className={styles.sectionTitle}>פרטי כרטיס אשראי</div>

            <div className={styles.fieldGroup}>
              <label>מספר כרטיס *</label>
              <input
                name="cardNumber"
                type="text"
                placeholder="XXXX XXXX XXXX XXXX"
                maxLength={19}
                value={chargeForm.cardNumber}
                onChange={handleChargeChange}
              />
            </div>

            <div className={styles.fieldsRow}>
              <div className={styles.fieldGroup}>
                <label>תאריך תפוגה</label>
                <div className={styles.expRow}>
                  <select
                    name="expMonth"
                    value={chargeForm.exp ? chargeForm.exp.split("/")[0] : ""}
                    onChange={(e) => {
                      const month = e.target.value;
                      const year = chargeForm.exp ? chargeForm.exp.split("/")[1] || "" : "";
                      setChargeForm((prev) => ({ ...prev, exp: month && year ? `${month}/${year}` : month ? `${month}/` : "" }));
                    }}
                  >
                    <option value="">חודש</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const m = String(i + 1).padStart(2, "0");
                      return <option key={m} value={m}>{m}</option>;
                    })}
                  </select>
                  <select
                    name="expYear"
                    value={chargeForm.exp ? chargeForm.exp.split("/")[1] || "" : ""}
                    onChange={(e) => {
                      const year = e.target.value;
                      const month = chargeForm.exp ? chargeForm.exp.split("/")[0] || "" : "";
                      setChargeForm((prev) => ({ ...prev, exp: month && year ? `${month}/${year}` : year ? `/${year}` : "" }));
                    }}
                  >
                    <option value="">שנה</option>
                    {Array.from({ length: 10 }, (_, i) => {
                      const y = String(new Date().getFullYear() % 100 + i).padStart(2, "0");
                      return <option key={y} value={y}>{`20${y}`}</option>;
                    })}
                  </select>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label>CVV</label>
                <input
                  name="cvv"
                  type="text"
                  placeholder="XXX"
                  maxLength={4}
                  value={chargeForm.cvv}
                  onChange={handleChargeChange}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label>ת.ז. בעל הכרטיס</label>
              <input
                name="holderId"
                type="text"
                placeholder="מספר תעודת זהות"
                value={chargeForm.holderId}
                onChange={handleChargeChange}
              />
            </div>

            <div className={styles.sectionTitle}>פרטי חשבונית</div>

            <div className={styles.fieldGroup}>
              <label>שם מלא לחשבונית</label>
              <input
                name="fullName"
                type="text"
                placeholder="שם מלא"
                value={chargeForm.fullName}
                onChange={handleChargeChange}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>ח.פ / ת.ז / ע.מ</label>
              <input
                name="companyId"
                type="text"
                placeholder="מספר מזהה"
                value={chargeForm.companyId}
                onChange={handleChargeChange}
              />
            </div>

            <div className={styles.fieldsRow}>
              <div className={styles.fieldGroup}>
                <label>כתובת</label>
                <input
                  name="address"
                  type="text"
                  placeholder="רחוב ומספר"
                  value={chargeForm.address}
                  onChange={handleChargeChange}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label>עיר</label>
                <input
                  name="city"
                  type="text"
                  placeholder="שם עיר"
                  value={chargeForm.city}
                  onChange={handleChargeChange}
                />
              </div>
            </div>

            <div className={styles.fieldsRow}>
              <div className={styles.fieldGroup}>
                <label>טלפון</label>
                <input
                  name="invoicePhone"
                  type="tel"
                  placeholder="050-0000000"
                  value={chargeForm.invoicePhone}
                  onChange={handleChargeChange}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label>דוא"ל</label>
                <input
                  name="email"
                  type="email"
                  placeholder="example@email.com"
                  value={chargeForm.email}
                  onChange={handleChargeChange}
                />
              </div>
            </div>

            <button
              className={styles.submitBtn}
              onClick={handleChargeSubmit}
              disabled={chargeSaving}
            >
              {chargeSaving ? "מבצע חיוב..." : "בצע חיוב"}
            </button>
          </div>
        </SidePanel>
      )}

      {/* INVOICE SIDE PANEL */}
      {isInvoiceFormOpen && (
        <SidePanel setIsOpen={setIsInvoiceFormOpen} title="הפקת מסמך">
          <div className={styles.formSection}>
            <div className={styles.fieldGroup}>
              <label>שם לקוח</label>
              <input
                name="clientName"
                type="text"
                placeholder="שם לקוח"
                value={invoiceForm.clientName}
                onChange={handleInvoiceChange}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>סכום (₪) *</label>
              <input
                name="amount"
                type="number"
                placeholder="0.00"
                value={invoiceForm.amount}
                onChange={handleInvoiceChange}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>תיאור השירות</label>
              <input
                name="description"
                type="text"
                placeholder="תיאור קצר של השירות"
                value={invoiceForm.description}
                onChange={handleInvoiceChange}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>ח.פ / ת.ז / ע.מ (אופציונלי)</label>
              <input
                name="clientTaxId"
                type="text"
                placeholder="מספר מזהה לחשבונית"
                value={invoiceForm.clientTaxId}
                onChange={handleInvoiceChange}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>סוג מסמך *</label>
              <select
                name="docType"
                value={invoiceForm.docType}
                onChange={handleInvoiceChange}
              >
                <option value="">בחר סוג מסמך</option>
                {morningAccount && morningAccount.data && morningAccount.data.allowedDocTypes
                  ? morningAccount.data.allowedDocTypes.map((dt) => (
                      <option key={dt.docTypeId} value={dt.type}>
                        {dt.label || DOC_TYPE_LABELS[dt.type] || dt.type}
                      </option>
                    ))
                  : Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
              </select>
            </div>

            <div className={styles.fieldGroup}>
              <label>אמצעי תשלום</label>
              <select
                name="paymentMethod"
                value={invoiceForm.paymentMethod}
                onChange={handleInvoiceChange}
              >
                {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.fieldGroup}>
              <label>מייל לשליחה (אופציונלי)</label>
              <input
                name="clientEmail"
                type="email"
                placeholder="example@email.com"
                value={invoiceForm.clientEmail}
                onChange={handleInvoiceChange}
              />
            </div>

            <div className={styles.fieldsRow}>
              <div className={styles.fieldGroup}>
                <label>טלפון</label>
                <input
                  name="clientPhone"
                  type="tel"
                  placeholder="050-0000000"
                  value={invoiceForm.clientPhone}
                  onChange={handleInvoiceChange}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label>עיר</label>
                <input
                  name="clientCity"
                  type="text"
                  placeholder="שם עיר"
                  value={invoiceForm.clientCity}
                  onChange={handleInvoiceChange}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label>כתובת</label>
              <input
                name="clientAddress"
                type="text"
                placeholder="רחוב ומספר"
                value={invoiceForm.clientAddress}
                onChange={handleInvoiceChange}
              />
            </div>

            <button
              className={styles.submitBtn}
              onClick={handleInvoiceSubmit}
              disabled={invoiceSaving}
            >
              {invoiceSaving ? "מפיק מסמך..." : "הפק מסמך"}
            </button>
          </div>
        </SidePanel>
      )}

      {isPlanDetailOpen && selectedPlan && (
        <PlanDetailPanel
          plan={selectedPlan}
          userId={id}
          setIsOpen={setIsPlanDetailOpen}
          onSuccess={() => {
            setIsPlanDetailOpen(false);
            setSelectedPlan(null);
            fetchClientPurchases();
          }}
        />
      )}
    </Layout>
  );
};

export default ClientBilling;
